import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["StepViewComponent", "showProgressIndicator", "showLogo", "showAccountLink"];
/* hs-eslint ignored failing-rules */

import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { getFullUrl } from 'hubspot-url-utils';
import { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutTemplate } from 'signup-components/components/LayoutTemplate/LayoutTemplate';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import styled from 'styled-components';
import { useChatBot } from '../chatbot/useChatBot';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
import { getCurrentStepNumbering } from '../flow-management/stepNumberingSelectors';
import { useCanGoBack } from '../hooks/useCanGoBack';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { getLoginUrl } from '../http-login/getLoginUrl';
import { useMessage } from '../messaging/useMessage';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getHaveAnAccountMessage } from '../step-landing-page/HaveAnAccountLink';
import { getLoginRedirectUrl } from '../step-landing-page/getLoginRedirectUrl';
import { goPrevStep } from '../store/flow/action-creators';
import { getPostAccountCreationSteps } from '../store/flow/selectors';
import { getActiveLoading } from '../store/loading-status/selectors';
import { useTracker } from '../tracking/useTracker';
import { getStepConfig } from '../views/getStepConfig';
import { AppFooterComponent } from './AppFooterComponent';
import { AppMessagingComponent } from './AppMessagingComponent';
import { getIsAuthenticated } from '../store/auth/selectors';
import { Step } from 'signup-constants/Step';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MessagingAboveStepContentContainer = styled.div.withConfig({
  displayName: "LayoutTemplateViewContainer__MessagingAboveStepContentContainer",
  componentId: "sc-2v1f13-0"
})(["display:flex;justify-content:center;"]);
export const LayoutTemplateViewContainer = _ref => {
  let {
      StepViewComponent,
      showProgressIndicator = true,
      showLogo = true,
      showAccountLink = true
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isActive: isInstanceOfSignupEmbedder,
    isEmbedded
  } = useSignupEmbedder();
  useChatBot();
  const isAuthed = useSelector(getIsAuthenticated);
  const {
    currentStep
  } = useCurrentStep();
  const loginRedirectUrl = getLoginRedirectUrl();
  const {
    canGoBack,
    completedStep
  } = useCanGoBack();
  const dispatch = useDispatch();
  const {
    currentStep: currentStepNumber,
    totalSteps
  } = useSelector(getCurrentStepNumbering);
  const activeLoading = useSelector(getActiveLoading);
  const {
    component
  } = useMessage();
  const Tracker = useTracker();
  const {
    isMobile
  } = useResponsiveness();
  const {
    url,
    altText,
    width: logoWidth,
    height: logoHeight
  } = getFlowConfig().getAppLogo(isMobile);
  const stepConfig = getStepConfig(currentStep);
  const showMessagingAboveContent = stepConfig && stepConfig.showMessagingAboveContent;
  const {
    layoutType,
    waveType,
    shouldShowProgressBar,
    decorativeBgComponent
  } = useLayoutTemplate();
  const alignCentredStepAtTheTop = isEmbedded && !activeLoading || stepConfig && stepConfig.alignCentredStepAtTheTop && !activeLoading;
  const shouldShowBackLink = Boolean(canGoBack && completedStep);
  const onBackLinkClick = event => {
    event.preventDefault();
    Tracker.backButton(currentStep, completedStep);
    dispatch(goPrevStep());
  };
  const onAccountLinkClick = () => {
    Tracker.signupInteraction('click-login-have-an-account', {}, true);
    Tracker.redirect({
      url: `${getFullUrl('app')}/login`
    });
  };
  const onLogoClick = () => {
    Tracker.signupInteraction('click-hubspot-company-logo', {
      currentStep
    }, true);
  };
  const renderMessagingAboveStepContent = () => {
    if (showMessagingAboveContent) {
      return /*#__PURE__*/_jsx(MessagingAboveStepContentContainer, {
        children: /*#__PURE__*/_jsx(AppMessagingComponent, {})
      });
    }
    return null;
  };
  const isOnLandingPageAndNonAuth = !isAuthed && currentStep === Step.LandingPage;
  const shouldShowHaveAnAccountLink = showAccountLink ? isOnLandingPageAndNonAuth : false;
  const postAccountCreation = useSelector(getPostAccountCreationSteps);
  const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
  const shouldShowManageCookiesLink = !isInstanceOfSignupEmbedder && !isPostAccountCreationStep;
  return /*#__PURE__*/_jsx("div", Object.assign({}, props, {
    children: /*#__PURE__*/_jsxs(LayoutTemplate, {
      accountLinkHref: getLoginUrl({
        redirectUrl: loginRedirectUrl
      }),
      accountLinkMessage: getHaveAnAccountMessage(),
      alignCentredStepAtTheTop: alignCentredStepAtTheTop,
      currentStepNumber: currentStepNumber,
      decorativeBgComponent: decorativeBgComponent,
      layoutType: layoutType,
      logoAltText: altText,
      logoHeight: logoHeight,
      logoImageSrc: url,
      logoWidth: logoWidth,
      messagingContent: component && component(),
      shouldAddTopPadding: !isEmbedded,
      shouldHalveProgressBar: getFlowConfig().shouldHalveProgressBar,
      showBackLink: shouldShowBackLink,
      showHaveAnAccountLink: shouldShowHaveAnAccountLink,
      showLogo: showLogo,
      showManageCookiesLink: shouldShowManageCookiesLink,
      showProgressIndicator: showProgressIndicator && shouldShowProgressBar,
      totalSteps: totalSteps,
      waveType: waveType,
      onAccountLinkClick: onAccountLinkClick,
      onBackLinkClick: onBackLinkClick,
      onLogoClick: onLogoClick,
      children: [renderMessagingAboveStepContent(), /*#__PURE__*/_jsxs(Suspense, {
        fallback: /*#__PURE__*/_jsx(UILoadingSpinner, {
          grow: true,
          size: "extra-small"
        }),
        children: [/*#__PURE__*/_jsx(StepViewComponent, {}), isInstanceOfSignupEmbedder && /*#__PURE__*/_jsx(AppFooterComponent, {})]
      })]
    })
  }));
};